import React, { useCallback, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'formik';
import { CheckBox } from 'components/Icons';
import { Button, Select, Switch } from 'components-antd';
import styles from './styles.module.scss';
import classnames from 'classnames';
import { Input as Input2, DatePicker, InputLabel } from 'components';
import CloseIcon from '../../icons/CrossIcon';
import EditIcon from '../../icons/PenIcon';
import BinIcon from '../../icons/BinIcon';

const FREQUENCY_OPTIONS = [
  'Daily',
  'Weekly',
  'Monthly',
  'Quarterly',
  'Biannually',
  'Yearly',
  'Biennial',
  'Triannually',
  'One Time',
  'Ad Hoc',
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const OCCURRENCES = ['First', 'Second', 'Third', 'Fourth', 'Last'];

export default function FrequencyConfigurator({ values, setFieldValue, isEdit }: any) {
  const [showErrMsg, setShowErrMsg] = useState<Record<number, boolean>>({});
  const [expressionInputValues, setExpressionInputValues] = useState<Record<number, string>>({});
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handleAdd = (arrayHelpers: FieldArrayRenderProps) => {
    arrayHelpers.push({
      FrequencyName: '',
      HasDayPreference: false,
      DayPreference: [],
      OccurrencePreference: [],
      HasSpecificDates: false,
      SpecificDates: [],
      ClosingDateRelated: false,
      EnrollmentDateRelated: false,
      OffsetOperator: null,
      OffsetValue: null,
      OffsetUnit: null,
    });
    setActiveIndex(values.frequencies.length);
  };

  const resetCDSpecificAndDayPref = (index) => {
    setFieldValue(`frequencies[${index}].HasSpecificDates`, false);
    setFieldValue(`frequencies[${index}].SpecificDates`, []);
    setFieldValue(`frequencies[${index}].ClosingDateRelated`, false);
    setFieldValue(`frequencies[${index}].EnrollmentDateRelated`, false);
    setFieldValue(`frequencies[${index}].OffsetOperator`, null);
    setFieldValue(`frequencies[${index}].OffsetValue`, null);
    setFieldValue(`frequencies[${index}].OffsetUnit`, null);
    setFieldValue(`frequencies[${index}].DayPreference`, []);
    setFieldValue(`frequencies[${index}].OccurrencePreference`, []);
    setFieldValue(`frequencies[${index}].HasDayPreference`, false);
    setShowErrMsg((prev) => ({ ...prev, [index]: false }));
  };

  const changeFrequencyName = useCallback((val: string, index: number) => {
    if (['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Biennial', 'Triannually'].includes(val)) {
      resetCDSpecificAndDayPref(index);
    }

    setFieldValue(`frequencies[${index}].FrequencyName`, val);
  }, []);

  return (
    <FieldArray name="frequencies">
      {(arrayHelpers) => {
        return (
          <>
            <div className={styles.configuratorContainer}>
              {values.frequencies.map((freq, index) => {
                const hasSpecificDates = freq.SpecificDates?.length > 0;
                const allowDayPref = freq.FrequencyName === 'Monthly';
                const allowDateControls =
                  freq.FrequencyName === 'Yearly' || freq.FrequencyName === 'One Time';
                const hasExpression = freq.OffsetOperator != null;
                const isEditing = activeIndex === index;

                const expressionInputValue =
                  expressionInputValues[index] ||
                  (hasExpression
                    ? `${freq.ClosingDateRelated ? 'CD' : 'E'}${freq.OffsetOperator}${
                        freq.OffsetValue
                      }${freq.OffsetUnit}`
                    : freq.HasSpecificDates && freq.SpecificDates?.length
                    ? freq.SpecificDates.join(', ')
                    : '');
                const isExisting = freq.isExisting;

                if (!isEditing) {
                  return (
                    <div key={index} className={styles.frequencyRowView}>
                      <div className={styles.summaryLine}>
                        <strong>{freq.FrequencyName || 'Frequency'} </strong>
                        <span>
                          {hasExpression
                            ? `(${expressionInputValue})`
                            : freq.HasSpecificDates
                            ? `(${freq.SpecificDates.join(', ')})`
                            : ''}
                        </span>
                      </div>
                      {isEdit && !isExisting && (
                        <div className={styles.actionBtns}>
                          <div className={styles.iconBtn} onClick={() => setActiveIndex(index)}>
                            <EditIcon />
                          </div>
                          <div
                            className={styles.iconBtn}
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            <CloseIcon />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div className={styles.frequencyRow} key={index}>
                    <div className={styles.inlineRow}>
                      <Select
                        large
                        disabled={false}
                        showArrow={true}
                        placeholder="Frequency"
                        getPopupContainer={(triggerNode) => triggerNode}
                        menuItemSelectedIcon={<CheckBox className={styles.checkBoxIcon} />}
                        className={styles.frequencySelect}
                        options={FREQUENCY_OPTIONS.map((f) => ({ label: f, value: f }))}
                        onChange={(val) => changeFrequencyName(val, index)}
                        value={freq.FrequencyName}
                      />
                      {allowDateControls && (
                        <div className={classnames(styles.freqPicker)}>
                          <div className={classnames(styles.fieldWrapper)}>
                            <div className={classnames(styles.freqDateWrapper)}>
                              <div className={classnames(styles.datePicker)}>
                                <DatePicker
                                  className={classnames(styles.templateControlPicker)}
                                  variant="light"
                                  value={freq.HasSpecificDates ? freq.SpecificDates : []}
                                  disabled={!allowDateControls}
                                  allowPlugin={false}
                                  onChange={(e) => {
                                    const rawValues: string[] = e.target.value;

                                    if (!rawValues || !rawValues.length) {
                                      setFieldValue(`frequencies[${index}].SpecificDates`, []);
                                      setFieldValue(
                                        `frequencies[${index}].HasSpecificDates`,
                                        false,
                                      );
                                      setFieldValue(
                                        `frequencies[${index}].ClosingDateRelated`,
                                        false,
                                      );
                                      setFieldValue(
                                        `frequencies[${index}].EnrollmentDateRelated`,
                                        false,
                                      );
                                      setFieldValue(`frequencies[${index}].OffsetOperator`, null);
                                      setFieldValue(`frequencies[${index}].OffsetValue`, null);
                                      setFieldValue(`frequencies[${index}].OffsetUnit`, null);
                                      setExpressionInputValues((prev) => ({
                                        ...prev,
                                        [index]: '',
                                      }));
                                      return;
                                    }

                                    const formattedDates = rawValues.map((v) => {
                                      const d = new Date(v);
                                      const day = d.getDate();
                                      const month = d.toLocaleString('en-US', { month: 'short' });
                                      return `${day} ${month}`;
                                    });

                                    setFieldValue(`frequencies[${index}].HasSpecificDates`, true);
                                    setFieldValue(
                                      `frequencies[${index}].SpecificDates`,
                                      formattedDates,
                                    );
                                    setFieldValue(
                                      `frequencies[${index}].ClosingDateRelated`,
                                      false,
                                    );
                                    setFieldValue(
                                      `frequencies[${index}].EnrollmentDateRelated`,
                                      false,
                                    );
                                    setFieldValue(`frequencies[${index}].OffsetOperator`, null);
                                    setFieldValue(`frequencies[${index}].OffsetValue`, null);
                                    setFieldValue(`frequencies[${index}].OffsetUnit`, null);
                                    setExpressionInputValues((prev) => ({ ...prev, [index]: '' }));
                                    setShowErrMsg((prev) => ({ ...prev, [index]: false }));
                                  }}
                                  placeholder="MM/DD"
                                  disablePlugins={true}
                                  options={{
                                    closeOnSelect: false,
                                    enableTime: false,
                                    mode: 'multiple',
                                    dateFormat: 'm-d',
                                    allowInput: true,
                                  }}
                                  monthOnly={true}
                                  closeOnOutsideClick={true}
                                  popoverCalender={true}
                                  placement="top"
                                  getPopupContainer={(node) => node}
                                  modalDatePicker={true}
                                  dateInputId="task-date-input"
                                />
                                <Input2
                                  variant={Input2.LIGHT_ROUND}
                                  placeholder="CD+2d OR E+2d"
                                  value={expressionInputValue}
                                  disabled={!allowDateControls}
                                  onChange={(e) => {
                                    const value = e.target.value.trim();
                                    setExpressionInputValues((prev) => ({
                                      ...prev,
                                      [index]: value,
                                    }));

                                    if (value === '') {
                                      setFieldValue(`frequencies[${index}].SpecificDates`, []);
                                      setFieldValue(
                                        `frequencies[${index}].HasSpecificDates`,
                                        false,
                                      );
                                      setFieldValue(
                                        `frequencies[${index}].ClosingDateRelated`,
                                        false,
                                      );
                                      setFieldValue(
                                        `frequencies[${index}].EnrollmentDateRelated`,
                                        false,
                                      );
                                      setFieldValue(`frequencies[${index}].OffsetOperator`, null);
                                      setFieldValue(`frequencies[${index}].OffsetValue`, null);
                                      setFieldValue(`frequencies[${index}].OffsetUnit`, null);
                                      return;
                                    }

                                    const match = value.match(/^(CD|E)([+-])(\d+)([dmy])$/i);
                                    if (match) {
                                      const [, type, operator, offset, unit] = match;
                                      setFieldValue(
                                        `frequencies[${index}].ClosingDateRelated`,
                                        type === 'CD',
                                      );
                                      setFieldValue(
                                        `frequencies[${index}].EnrollmentDateRelated`,
                                        type === 'E',
                                      );
                                      setFieldValue(
                                        `frequencies[${index}].OffsetOperator`,
                                        operator,
                                      );
                                      setFieldValue(
                                        `frequencies[${index}].OffsetValue`,
                                        parseInt(offset, 10),
                                      );
                                      setFieldValue(
                                        `frequencies[${index}].OffsetUnit`,
                                        unit.toLowerCase(),
                                      );
                                      setFieldValue(
                                        `frequencies[${index}].HasSpecificDates`,
                                        false,
                                      );
                                      setFieldValue(`frequencies[${index}].SpecificDates`, []);
                                      setFieldValue(
                                        `frequencies[${index}].HasDayPreference`,
                                        false,
                                      );
                                      setFieldValue(`frequencies[${index}].DayPreference`, []);
                                      setFieldValue(
                                        `frequencies[${index}].OccurrencePreference`,
                                        [],
                                      );
                                      setShowErrMsg((prev) => ({ ...prev, [index]: false }));
                                    } else {
                                      setShowErrMsg((prev) => ({ ...prev, [index]: true }));
                                    }
                                  }}
                                  saveOnChange={false}
                                  className={styles.datePickerInput}
                                  id="task-date-input"
                                />
                                {showErrMsg?.[index] && (
                                  <div className={styles.errorMsg}>Invalid Expression Format</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div
                        className={classnames(styles.iconBtn, styles.binIcon)}
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        <BinIcon />
                      </div>
                    </div>

                    {allowDayPref && (
                      <div className={styles.freqMiddleRow}>
                        <div>
                          <label> Day Preference: </label>
                          <Switch
                            checked={freq.HasDayPreference}
                            disabled={hasSpecificDates || !allowDayPref}
                            onChange={(val) =>
                              setFieldValue(`frequencies[${index}].HasDayPreference`, val)
                            }
                          />
                        </div>
                      </div>
                    )}

                    {freq.HasDayPreference && !hasSpecificDates && (
                      <div className={styles.dayPrefSection}>
                        <Select
                          large
                          mode="multiple"
                          placeholder="Days"
                          value={freq.DayPreference || []}
                          menuItemSelectedIcon={<CheckBox className={styles.checkBoxIcon} />}
                          onChange={(val) =>
                            setFieldValue(`frequencies[${index}].DayPreference`, val)
                          }
                          className={styles.daySelect}
                          options={DAYS.map((d) => ({
                            label: d,
                            value: d,
                          }))}
                        ></Select>
                        <Select
                          large
                          mode="multiple"
                          placeholder="Occurrences"
                          value={freq.OccurrencePreference || []}
                          menuItemSelectedIcon={<CheckBox className={styles.checkBoxIcon} />}
                          onChange={(val) =>
                            setFieldValue(`frequencies[${index}].OccurrencePreference`, val)
                          }
                          className={styles.daySelect}
                          options={OCCURRENCES.map((o) => ({
                            label: o,
                            value: o,
                          }))}
                        ></Select>
                      </div>
                    )}
                  </div>
                );
              })}

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAdd(arrayHelpers);
                }}
                className={styles.addFrequencyBtn}
              >
                + Add Frequency
              </button>
            </div>
          </>
        );
      }}
    </FieldArray>
  );
}
